import React, { useState, useMemo, useCallback, useEffect, useRef } from "react";
import { Table, Button, Input, Switch, Select, Dropdown, Checkbox, Slider, Tooltip } from "antd";
import { ControlOutlined, FilterOutlined, ClearOutlined, CopyOutlined } from "@ant-design/icons";
import columns from "./columns";
import NoDataComponent from "../common/NoDataComponent";
import KitDrawer from "../KitDetailView/KitDrawer";
import MultiKitDrawer from "../KitDetailView/MultiKitDrawer";
import CreateKitDrawer from "../KitDetailView/CreateKit/CreateKitDrawer"
import CreateMultiKitDrawer from "../KitDetailView/CreateMultiKit/CreateMultiKitDrawer"
import apiService from "./data";
import FullPageLoader from "../common/FullPageLoader";
import ErrorNotification from "../common/ErrorNotification";
import { exportToExcel } from "../MasterTable/exportToExcel";
import globalData from "../../config/global_data";
import { useAuth } from "../UserAuth/AuthContext";

const { Search } = Input;
const { apidata } = apiService;

const KitTable = ({ globalList, setGlobalList  }) => {
  const [data, setData] = useState([]);
  const [originaldata, setOriginaldata] = useState();
  const [searchText, setSearchText] = useState("");
  const [drawerVisible, setDrawerVisible] = useState(false);
  const [createDrawerVisible, setcreateDrawerVisible] = useState(false);
  const [createMultiDrawerVisible, setcreateMultiDrawerVisible] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [visibleColumns, setVisibleColumns] = useState(columns.map((col) => col.dataIndex));
  const [isloader, setisloader] = useState(true);
  const [alert, setAlert] = useState({});
  const [stores, setstores] = useState([]);
  const [slectedStore, setSlectedStore] = useState("Select Store");
  const [selectedDrawerdata, setSelectedDrawerdata] = useState({});
  const [selectedFilter, setSelectedFilter] = useState(null);
  const [stickyHeader, setStickyHeader] = useState(false);
  const tableWrapperRef = useRef(null);
  const isAnyFilterApplied = searchText || (slectedStore && slectedStore !== "Select Store") || selectedFilter;
  const [minPrice, setMinPrice] = useState(0);
  const [maxPrice, setMaxPrice] = useState(100);
  const [selectedPriceRange, setSelectedPriceRange] = useState([0, 100]);
  const [copiedSku, setCopiedSku] = useState(null);
  const { userData } = useAuth();

  const handleExport = () => {
    const selectedData = data.filter((item) =>
      selectedRowKeys.includes(item.key)
    );

    const mappedData = selectedData.map((item) => {
     const secondarySkus = Array.isArray(item.bc_secondary_sku)
      ? item.bc_secondary_sku.map((skuObj) => skuObj.value).join(", ")
      : "";
      return {
        "Store": item.store,
        "Kit Name": item.kitname,
        "Kit Name": item.kitname,
        "BC Product (SKU)": item.bcSku,
        "Active On Store": item.active ? "True" : "False",
        "BC MSRP": item.msrp,
        "BC Sale Price": item.sale_price,
        "BC Primary Product (Kit)": item.bc_primary_sku?.value,
        "BC Secondary Products (SKUs)": secondarySkus,  
        "Discount": item.discount.value, 
        "Enrichment": item.enrichment ? "True" : "False",
        "Last Updated": item.lastUpdated,
      };
    });


    exportToExcel(mappedData);
  };

  useEffect(() => {

    const handleScroll = () => {
      if (tableWrapperRef.current) {
        const scrollTop = tableWrapperRef.current.scrollTop;
        setStickyHeader(scrollTop > 0); // enable sticky only when scrolling starts
      }
    };

    const wrapper = tableWrapperRef.current;
    if (wrapper) {
      wrapper.addEventListener('scroll', handleScroll);
    }

    return () => {
      if (wrapper) {
        wrapper.removeEventListener('scroll', handleScroll);
      }
    };
  }, []);

  useEffect(() => {
    if (!globalList || !globalList.kit_datalist || globalList.kit_datalist.length <= 0) {

      fetchData();
    }

  }, []);
  useEffect(() => {
    if (globalList && globalList.kit_datalist) {
      setData(globalList.kit_datalist);
      setOriginaldata(globalList.kit_datalist);
      applyAllFilters({ original: globalList.kit_datalist })
      setisloader(false)
    }
  }, [globalList]);

  const fetchData = async () => {
    const apiData = await apidata();

    if (apiData.length > 0) {
      setData(apiData);
      setOriginaldata(apiData);
      setGlobalList({ ...globalList, kit_datalist: apiData })
      const anyFiltersApplied = isAnyFilterApplied;
      if (anyFiltersApplied) {
        applyAllFilters({ original: apiData });
      }

      setisloader(false);
      const msrps = apiData
        .map(item => parseFloat(item.msrp?.replace(/[^0-9.]/g, '')))
        .filter(price => !isNaN(price));

      if (msrps.length) {
        const min = Math.floor(Math.min(...msrps));
        const max = Math.ceil(Math.max(...msrps));
        setMinPrice(min);
        setMaxPrice(max);
        setSelectedPriceRange([min, max]);
      }
    }
    
    const storelist = await apiService.get_stores();
    if (storelist.length > 0) {
      const allOption = { label: 'All', value: 'all' };
      setstores([allOption, ...storelist]);
    }
  };

  const openDrawer = useCallback((key) => {

    const filteredData = originaldata.filter((item) =>
      item.key === key
    );

    setSelectedDrawerdata(filteredData[0]);
    setDrawerVisible(true);

  }, [originaldata]);

  const closeDrawer = () => setDrawerVisible(false);

  const createKitOpenDrawer = useCallback((key) => {

    setcreateDrawerVisible(true);

  }, [originaldata]);

  const createKitCloseDrawer = () => setcreateDrawerVisible(false);

  const createMultiKitOpenDrawer = useCallback((key) => {

    setcreateMultiDrawerVisible(true);

  }, [originaldata]);

  const createMultiKitCloseDrawer = () => setcreateMultiDrawerVisible(false);

  const handleSearch = (value) => {
    setSearchText(value);
    applyAllFilters({ search: value });
  };

  const activeFiltersCount = [
    searchText !== '',
    slectedStore && slectedStore !== 'all' && slectedStore !== 'Select Store',
    selectedFilter !== null,
  ].filter(Boolean).length;

  const handleStoreChange = (value) => {
    setSlectedStore(value);
    applyAllFilters({ store: value });
  };

  const handleFilterChange = (value) => {
    setSelectedFilter(value);
    applyAllFilters({ filter: value });
  };

  const applyAllFilters = async (filters = {}) => {
    const {
      search = searchText,
      store = slectedStore,
      filter = selectedFilter,
      priceRange = selectedPriceRange,
      original = originaldata,
    } = filters;

    let filtered = await original;

    // Filter by store
    if (store !== "" && store !== "all" && store !== "Select Store") {
      filtered = filtered.filter(item =>
        item.store.toLowerCase().includes(store.toLowerCase())
      );
    }

    // Filter by search text

    if (search !== "") {
      filtered = await filtered.filter(item =>
        item.bcSku?.toLowerCase().includes(search.toLowerCase()) ||
        item.kitname?.toLowerCase().includes(search.toLowerCase())
      );
    }



    switch (filter) {
      case "All":
        setSelectedPriceRange([minPrice, maxPrice]);
        // filtered = originaldata
        break;
      case "Products not live":
        setSelectedPriceRange([minPrice, maxPrice]);
        filtered = filtered.filter(item => item.active === 0);
        break;
      case "Products live":
        setSelectedPriceRange([minPrice, maxPrice]);
        filtered = filtered.filter(item => item.active === 1);
        break;
      case "Enrichment pending":
        setSelectedPriceRange([minPrice, maxPrice]);
        filtered = filtered.filter(item => !item.enrichment || item.enrichment === false || item.enrichment === "false");
        break;
      case "Low Stock":
        setSelectedPriceRange([minPrice, maxPrice]);
        filtered = filtered.filter(item => item.bcInventory < 5 && item.bcInventory !== "NA");
        break;
      case "Price":
        filtered = filtered.filter(item => {
          const msrp = parseFloat(item.msrp?.replace(/[^0-9.]/g, ''));
          return !isNaN(msrp) && msrp >= priceRange[0] && msrp <= priceRange[1];
        });
        break;
      case "Multi Kit":
        setSelectedPriceRange([minPrice, maxPrice]);
        filtered = filtered.filter(item => Number(item.is_multi_kit_prod) === 1);
        break;
      case "Single Kit":
        setSelectedPriceRange([minPrice, maxPrice]);
        filtered = filtered.filter(item => Number(item.is_multi_kit_prod) === 0);
        break;
      // case "Incomplete Mapping":
      //   setSelectedPriceRange([minPrice, maxPrice]);
      //   filtered = filtered.filter(item => item.sapSku === "NA");
      //   break;
      default:
        break;
    }
    setData(filtered);
    // setCurrentPage(1);

  };

  const handleAlert = async (type, title, message, isopen) => {
    setAlert({ type, title, message, isopen });
    setTimeout(() => {
      setAlert({ isopen: false });
    }, 100);
  };

  const handleToggleActive = useCallback(async (checked, key) => {
    setisloader(true);
    let filterdata = originaldata.find(item => item.key === key);
    let responsedata = await apiService.active_inactive_status(filterdata, checked);
    if (responsedata) {
      setData(prevData =>
        prevData.map(item =>
          item.key === key ? { ...item, active: checked } : item
        )
      );
      await fetchData(responsedata);
      setisloader(false);
      await handleAlert("success", "Success!", responsedata.data.message, true);
    } else {
      await fetchData(responsedata);
      setisloader(false);
      await handleAlert("error", "Fail!", "Something went wrong.", true);
    }
  }, [originaldata, fetchData]);



  const handleColumnToggle = (dataIndex) => {
    setVisibleColumns((prev) =>
      prev.includes(dataIndex)
        ? prev.filter((col) => col !== dataIndex)
        : [...prev, dataIndex]
    );
  };

  const columnMenu = {
    items: columns.map(col => ({
      key: col.dataIndex,
      label: (
        <Checkbox
          checked={visibleColumns.includes(col.dataIndex)}
          onChange={() => handleColumnToggle(col.dataIndex)}
        >
          {col.title}
        </Checkbox>
      ),
    })),
  };

  const updatedColumns = useMemo(() => {
    return columns
      .filter((col) => visibleColumns.includes(col.dataIndex))
      .map((col) => {
        if (col.dataIndex === "active") {
          return {
            ...col,
            render: (_, record) => (
              <Switch
                checked={record.active}
                disabled={userData.data?.access !== "ALL"}
                onChange={(checked) => handleToggleActive(checked, record.key)}
              />
            ),
          };
        }
        if (col.dataIndex === "kitname") {
          return {
            ...col,
            render: (_, record) => (
              <span
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  position: 'relative',
                }}
                onMouseEnter={(e) => {
                  const icon = e.currentTarget.querySelector('.copy-icon');
                  if (icon) icon.style.opacity = 1;
                }}
                onMouseLeave={(e) => {
                  const icon = e.currentTarget.querySelector('.copy-icon');
                  if (icon) icon.style.opacity = 0;
                }}
              >
                <Button
                  type="link"
                  onClick={() => openDrawer(record.key)}
                  style={{ padding: 0 }}
                >
                  {record.kitname}
                </Button>
                <Tooltip title={copiedSku === record.kitname ? "Copied" : "Copy"}>
                  <CopyOutlined
                    className="copy-icon"
                    onClick={() => {
                      navigator.clipboard.writeText(record.kitname);
                      setCopiedSku(record.kitname);
                      setTimeout(() => setCopiedSku(null), 1500);
                    }}
                    style={{
                      marginLeft: 6,
                      color: copiedSku === record.kitname ? '#52c41a' : '#999',
                      cursor: 'pointer',
                      opacity: 0,
                      transition: 'opacity 0.3s ease, color 0.3s ease',
                    }}
                  />
                </Tooltip>
              </span>
            )
          };
        }
        return col;
      });
  }, [handleToggleActive, visibleColumns, copiedSku]);

  const rowSelection = {
    selectedRowKeys,
    onChange: (selectedKeys) => {
      setSelectedRowKeys(selectedKeys);
    },
  };

  return (
    <div>
      {/* Filter and Search Bar */}
      <div style={{ marginBottom: 16 }} className="TableFilterBox">
        {userData?.data?.access === "ALL" && (
          <>
            <Button type="primary" onClick={e => createKitOpenDrawer()}>
              Create Kit
            </Button>
            <Button type="primary" onClick={e => createMultiKitOpenDrawer()}>
              Create Multi Kit
            </Button>
          </>
        )}
        <Select
          placeholder="Filter"
          style={{ width: 180 }}
          suffixIcon={<FilterOutlined style={{ color: "rgba(0, 0, 0, 0.88)" }} />}
          value={selectedFilter}
          className="filterSelect"
          onChange={handleFilterChange}
          options={[
            { value: "All", label: "All" },
            { value: "Products not live", label: "Products not live" },
            { value: "Products live", label: "Products live" },
            // { value: "Incomplete Mapping", label: "Incomplete Mapping" },
            { value: "Enrichment pending", label: "Enrichment pending" },
            { value: "Price", label: "Price" },
            { value: "Multi Kit", label: "Multi Kit" },
            { value: "Single Kit", label: "Single Kit" }
          ]}
        />
        {selectedFilter === "Price" && (
          <Slider
            className="rangeSlider"
            range
            step={1}
            min={minPrice}
            max={maxPrice}
            value={selectedPriceRange}
            onChange={(value) => {
              setSelectedPriceRange(value);
              applyAllFilters({ priceRange: value });
            }}
            tooltip={{ className: "TblTooltip" }}
          />
        )}
        <Select
          placeholder="Stores"
          style={{ width: 120 }}
          value={slectedStore}
          options={stores}
          onChange={handleStoreChange}
        />
        <Search
          placeholder="Search by BC SKU or Name"
          value={searchText}
          onChange={(e) => handleSearch(e.target.value)}
          style={{ width: 270 }}
          allowClear
          enterButton
        />
        {isAnyFilterApplied && (
          <Button
            onClick={() => {
              setSearchText('');
              setSlectedStore('Select Store');
              setSelectedFilter(null);
              setData(originaldata);
              // setCurrentPage(1); // Optionally reset to page 1
            }}
            className="ClearAllFiltersBtn"

          >
            <ClearOutlined style={{ marginRight: 4 }} />
            {activeFiltersCount === 1 ? 'Clear Filters' : 'Clear Filters'}
          </Button>
        )}
        <Dropdown menu={{ ...columnMenu, className: 'ColumnMenuClass' }} trigger={["click"]} placement="bottomRight" className="TableSettings">
          <Button icon={<ControlOutlined />} />
        </Dropdown>
        <div className="RightBox" style={{ display: "flex", gap: 8 }}>
          <Button type="primary" disabled={selectedRowKeys.length <= 0} onClick={handleExport}>
            Export Selected
          </Button>
        </div>
      </div>

      <FullPageLoader isloader={isloader} />
      <ErrorNotification {...alert} />
      {/* Table */}
      <div
        className="TableBox"
        ref={tableWrapperRef}
        style={{ maxHeight: 700, overflow: 'auto' }}
      >
        {data && data.length > 0 ? <Table
          rowSelection={rowSelection}
          columns={updatedColumns}
          dataSource={data}
          scroll={{ x: '100%', y: 500 }}
          pagination={{
            current: currentPage,
            pageSize: pageSize,
            total: data.length,
            onChange: (page, size) => {
              setCurrentPage(page);
              setPageSize(size);
            },
            showTotal: (total) =>
              `Total ${total} items${selectedRowKeys.length > 0 ? ` | Selected: ${selectedRowKeys.length}` : ''}`,
            showSizeChanger: true,
            pageSizeOptions: ['10', '20', '50', '100'],
            position: ['bottomLeft']
          }}
          bordered
          sticky={stickyHeader ? { offsetHeader: 0 } : false}
        /> : <div>
          <NoDataComponent />
        </div>}
      </div>
      {/* Edit Kit Drawer Start */}
      {drawerVisible && (Number(selectedDrawerdata?.is_multi_kit_prod) === 1 ? (
        <MultiKitDrawer onProductCreate={fetchData} visible={drawerVisible} kidata={selectedDrawerdata} onClose={closeDrawer} setisloader={setisloader} />
      ) : (
        <KitDrawer onProductCreate={fetchData} visible={drawerVisible} kidata={selectedDrawerdata} onClose={closeDrawer} setisloader={setisloader} />
      ))}

      {/* Create Kit Drawer Start */}
      {createDrawerVisible && <CreateKitDrawer visible={createDrawerVisible} onClose={createKitCloseDrawer} onProductCreate={fetchData} setisloader={setisloader} />}
      {createMultiDrawerVisible && <CreateMultiKitDrawer visible={createMultiDrawerVisible} onClose={createMultiKitCloseDrawer} onProductCreate={fetchData} setisloader={setisloader} />}
    </div>
  );
};

export default KitTable;
